import { BaseHTMLElement } from '../BaseHTMLElement';

export class LaunchingPage extends BaseHTMLElement {
  private onGetStarted: () => void;
  private emailList: string[] = [];

  constructor(onGetStarted: () => void) {
    super('div', 'launching-page');
    this.onGetStarted = onGetStarted;
    this.render();
  }

  private render(): void {
    this.element.innerHTML = `
      <div class="launching-container">
        <header class="launching-header" role="banner">
          <div class="logo">
            <h1>Salmon</h1>
            <p>Coming Soon - Creative Canvas Platform</p>
          </div>
        </header>

        <main class="launching-main" role="main">
          <section class="hero" aria-labelledby="hero-heading">
            <div class="hero-content">
              <h2 id="hero-heading">Something Amazing is Coming</h2>
              <p>We're building the future of creative collaboration. Join our early access list to be the first to experience Salmon's powerful canvas-based design platform.</p>
              
              ${this.getEnvVariable('VITE_ENABLE_EMAIL_SIGNUP', 'true') === 'true' ? `
              <div class="email-signup" aria-labelledby="signup-heading">
                <h3 id="signup-heading">Get Early Access</h3>
                <p>Be among the first to create, collaborate, and share your creative vision with Salmon.</p>
                
                <form class="email-form" id="email-signup-form">
                  <div class="form-group">
                    <input 
                      type="email" 
                      id="email-input" 
                      placeholder="Enter your email address" 
                      required
                      aria-label="Email address for early access"
                    >
                    <button type="submit" class="btn-primary" id="signup-btn" aria-label="Join early access list">
                      Join Early Access
                    </button>
                  </div>
                </form>
                
                <div class="signup-status" id="signup-status" aria-live="polite"></div>
                
                <div class="get-started-section">
                  <p>Already have access or want to explore now?</p>
                  <button class="btn-outline" id="get-started-btn" aria-label="Get started with Salmon">
                    Get Started
                  </button>
                </div>
              </div>
              ` : `
              <div class="get-started-section">
                <p>Ready to get started?</p>
                <button class="btn-primary" id="get-started-btn" aria-label="Get started with Salmon">
                  Get Started
                </button>
              </div>
              `}

              <div class="features-preview">
                <h4>What to Expect</h4>
                <div class="feature-list">
                  <div class="feature-item">
                    <span class="feature-icon">🎨</span>
                    <span>Intuitive Design Tools</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">⚡</span>
                    <span>Real-time Collaboration</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">📱</span>
                    <span>Cross-Platform Support</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">🔒</span>
                    <span>Secure & Private</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="hero-visual" aria-label="Salmon canvas interface preview">
              <div class="canvas-preview">
                <div class="canvas-mockup">
                  <div class="mockup-shape shape-1" aria-hidden="true"></div>
                  <div class="mockup-shape shape-2" aria-hidden="true"></div>
                  <div class="mockup-shape shape-3" aria-hidden="true"></div>
                  <div class="mockup-shape shape-4" aria-hidden="true"></div>
                </div>
                <div class="coming-soon-badge">
                  <span>Coming Soon</span>
                </div>
              </div>
            </div>
          </section>

          ${this.getEnvVariable('VITE_ENABLE_COUNTDOWN', 'true') === 'true' ? `
          <section class="countdown" aria-labelledby="countdown-heading">
            <h3 id="countdown-heading">Launch Countdown</h3>
            <div class="countdown-timer" id="countdown-timer">
              <div class="time-unit">
                <span class="time-value" id="days">00</span>
                <span class="time-label">Days</span>
              </div>
              <div class="time-unit">
                <span class="time-value" id="hours">00</span>
                <span class="time-label">Hours</span>
              </div>
              <div class="time-unit">
                <span class="time-value" id="minutes">00</span>
                <span class="time-label">Minutes</span>
              </div>
              <div class="time-unit">
                <span class="time-value" id="seconds">00</span>
                <span class="time-label">Seconds</span>
              </div>
            </div>
          </section>
          ` : ''}

          <section class="social-proof" aria-labelledby="social-proof-heading">
            <h3 id="social-proof-heading">Join the Community</h3>
            <div class="stats">
              <div class="stat-item">
                <span class="stat-number" id="signup-count">${this.emailList.length}</span>
                <span class="stat-label">Early Access Signups</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">500+</span>
                <span class="stat-label">Designers Interested</span>
              </div>
            </div>
          </section>
        </main>

        <footer class="launching-footer" role="contentinfo">
          <div class="footer-content">
            <p>&copy; 2024 Salmon. All rights reserved.</p>
            <div class="footer-links">
              <a href="#privacy" aria-label="Privacy policy">Privacy Policy</a>
              <a href="#terms" aria-label="Terms of service">Terms of Service</a>
              <a href="#contact" aria-label="Contact us">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    `;

    this.setupEventListeners();
    this.loadEmailList();
    
    // Only start countdown if it's enabled
    if (this.getEnvVariable('VITE_ENABLE_COUNTDOWN', 'true') === 'true') {
      this.startCountdown();
    }
  }

  private setupEventListeners(): void {
    const emailForm = this.element.querySelector('#email-signup-form') as HTMLFormElement;
    const emailInput = this.element.querySelector('#email-input') as HTMLInputElement;
    const getStartedBtn = this.element.querySelector('#get-started-btn') as HTMLButtonElement;

    // Only set up email form listeners if email signup is enabled
    if (this.getEnvVariable('VITE_ENABLE_EMAIL_SIGNUP', 'true') === 'true' && emailForm && emailInput) {
      emailForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleEmailSignup(emailInput.value.trim());
      });

      // Real-time validation
      emailInput.addEventListener('input', () => {
        this.validateEmail(emailInput.value);
      });
    }

    // Get started button (always present)
    if (getStartedBtn) {
      getStartedBtn.addEventListener('click', () => {
        this.onGetStarted();
      });
    }
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    const emailInput = this.element.querySelector('#email-input') as HTMLInputElement;
    
    if (email && !isValid) {
      emailInput.style.borderColor = '#e74c3c';
    } else {
      emailInput.style.borderColor = '#e1e5e9';
    }
    
    return isValid;
  }

  private handleEmailSignup(email: string): void {
    if (!this.validateEmail(email)) {
      this.showStatus('Please enter a valid email address.', 'error');
      return;
    }

    if (this.emailList.includes(email)) {
      this.showStatus('You\'re already on our early access list!', 'info');
      return;
    }

    // Add to email list
    this.emailList.push(email);
    
    // Update signup count
    const signupCount = this.element.querySelector('#signup-count');
    if (signupCount) {
      signupCount.textContent = this.emailList.length.toString();
    }

    // Show success message
    this.showStatus('Thank you! You\'re now on our early access list. We\'ll notify you when Salmon launches.', 'success');
    
    // Clear the form
    const emailInput = this.element.querySelector('#email-input') as HTMLInputElement;
    emailInput.value = '';

    // Store in localStorage for persistence
    this.saveEmailList();
  }

  private showStatus(message: string, type: 'success' | 'error' | 'info'): void {
    const statusElement = this.element.querySelector('#signup-status') as HTMLElement;
    statusElement.textContent = message;
    statusElement.className = `signup-status ${type}`;
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      statusElement.textContent = '';
      statusElement.className = 'signup-status';
    }, 5000);
  }

  private saveEmailList(): void {
    try {
      localStorage.setItem('salmon-email-list', JSON.stringify(this.emailList));
    } catch (error) {
      console.warn('Could not save email list to localStorage:', error);
    }
  }

  private loadEmailList(): void {
    try {
      const saved = localStorage.getItem('salmon-email-list');
      if (saved) {
        this.emailList = JSON.parse(saved);
        const signupCount = this.element.querySelector('#signup-count');
        if (signupCount) {
          signupCount.textContent = this.emailList.length.toString();
        }
      }
    } catch (error) {
      console.warn('Could not load email list from localStorage:', error);
    }
  }

  private startCountdown(): void {
    // Get launch date from environment variable or default to 3 months from now
    const launchDateStr = this.getEnvVariable('VITE_LAUNCH_DATE');
    let launchDate: Date;
    
    if (launchDateStr) {
      launchDate = new Date(launchDateStr);
    } else {
      // Default to 3 months from now
      launchDate = new Date();
      launchDate.setMonth(launchDate.getMonth() + 3);
    }
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance < 0) {
        // Launch time reached
        const countdownTimer = this.element.querySelector('#countdown-timer');
        if (countdownTimer) {
          countdownTimer.innerHTML = '<div class="launch-ready">🚀 We\'re Live!</div>';
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const daysElement = this.element.querySelector('#days');
      const hoursElement = this.element.querySelector('#hours');
      const minutesElement = this.element.querySelector('#minutes');
      const secondsElement = this.element.querySelector('#seconds');

      if (daysElement) daysElement.textContent = days.toString().padStart(2, '0');
      if (hoursElement) hoursElement.textContent = hours.toString().padStart(2, '0');
      if (minutesElement) minutesElement.textContent = minutes.toString().padStart(2, '0');
      if (secondsElement) secondsElement.textContent = seconds.toString().padStart(2, '0');
    };

    // Update immediately and then every second
    updateCountdown();
    setInterval(updateCountdown, 1000);
  }

  public getEmailList(): string[] {
    return [...this.emailList];
  }

  public getEmailCount(): number {
    return this.emailList.length;
  }

  private getEnvVariable(key: string, defaultValue: string = ''): string {
    // In Vite, environment variables are available on import.meta.env
    return import.meta.env[key] || defaultValue;
  }
}
